import { useMutation, useQueryClient } from '@tanstack/react-query';
import { INPUTS_QUERY_KEY } from 'constants/queryKeys';
import { IInput } from 'interfaces/IInput';
import { IInputTask } from 'interfaces/IInputTask';
import InputService from 'services/InputService';

interface UseUpsertInputTaskArgs {
  activeOrganisationId: string | null;
}

interface UpsertInputTaskVariables {
  inputId: string;
  task: IInputTask;
}

interface UpsertInputTaskContext {
  /** The task as it was before this save, to put back if the save fails. */
  previousTask: IInputTask | null;
  /** True when the task wasn't on the plan yet — a failed save takes it off again. */
  isNew: boolean;
}

/**
 * Saves one whole task on any plan — not only the plan open in Planning —
 * and keeps the org's inputs cache in step, so every view reading it (tree,
 * Insights, Planning) updates without a refetch. The same per-type upsert
 * the Tasks grid uses (usePendingTaskSave), without its debounce: callers
 * save on commit.
 *
 * The change shows straight away: the task is written into the cache before
 * the request goes, replaced by the server's copy of the plan on success,
 * and put back as it was on failure. Only that one task is touched either
 * way, so saves to other plans or tasks in flight at the same time aren't
 * undone by a rollback.
 *
 * A task that isn't on the plan yet is added (the Nutrition lens adds new
 * applications, OP-2523), and taken off again if its save fails.
 *
 * The task is sent whole, so callers must build it from the latest cached
 * copy of the plan and must not send a second save for the same task while
 * one is in flight.
 */
export const useUpsertInputTask = ({
  activeOrganisationId,
}: UseUpsertInputTaskArgs) => {
  const queryClient = useQueryClient();
  const queryKey = [INPUTS_QUERY_KEY, activeOrganisationId];

  const replaceTask = (inputId: string, task: IInputTask) =>
    queryClient.setQueryData<IInput[]>(queryKey, (current) =>
      current?.map((input) => {
        if (input.id !== inputId) return input;
        const tasks = input.tasks ?? [];
        return {
          ...input,
          tasks: tasks.some((t) => t.taskId === task.taskId)
            ? tasks.map((t) => (t.taskId === task.taskId ? task : t))
            : [...tasks, task],
        };
      })
    );

  const removeTask = (inputId: string, taskId: string) =>
    queryClient.setQueryData<IInput[]>(queryKey, (current) =>
      current?.map((input) =>
        input.id === inputId
          ? {
              ...input,
              tasks: (input.tasks ?? []).filter((t) => t.taskId !== taskId),
            }
          : input
      )
    );

  return useMutation<
    Awaited<ReturnType<typeof InputService.UpsertTask>>,
    unknown,
    UpsertInputTaskVariables,
    UpsertInputTaskContext
  >({
    mutationFn: async ({ inputId, task }) => {
      const clientNow = new Date().toISOString();
      return InputService.UpsertTask(inputId, task, clientNow);
    },
    onMutate: async ({ inputId, task }) => {
      if (!activeOrganisationId) return { previousTask: null, isNew: false };
      // A refetch landing mid-save would overwrite the change shown below.
      await queryClient.cancelQueries({ queryKey });
      const previousTask =
        queryClient
          .getQueryData<IInput[]>(queryKey)
          ?.find((input) => input.id === inputId)
          ?.tasks?.find((t) => t.taskId === task.taskId) ?? null;
      replaceTask(inputId, task);
      return { previousTask, isNew: previousTask === null };
    },
    onError: (_error, { inputId, task }, context) => {
      if (!activeOrganisationId || !context) return;
      if (context.previousTask) replaceTask(inputId, context.previousTask);
      else if (context.isNew) removeTask(inputId, task.taskId);
    },
    onSuccess: (response) => {
      // The server returns the saved plan, so it's written straight in rather
      // than invalidated: a refetch of every plan would be slower and could
      // land on top of another save still in flight.
      const updatedInput = response?.data?.[0];
      if (!updatedInput || !activeOrganisationId) return;

      queryClient.setQueryData<IInput[]>(queryKey, (current) => {
        if (!current) return current;
        return current.map((item) =>
          item.id === updatedInput.id ? updatedInput : item
        );
      });
    },
  });
};

export default useUpsertInputTask;
